import { useEffect, useState } from "react";
import api from "@/lib/api";
import { getClientId } from "@/lib/clientId";

/**
 * Public heart/like button for a journal post.
 * One like per browser (client_id). Optimistic toggle with server reconcile.
 */
export default function LikeButton({ slug, initialViews = 0 }) {
  const [likes, setLikes] = useState(0);
  const [views, setViews] = useState(initialViews);
  const [liked, setLiked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/posts/${slug}/engagement`, {
          params: { client_id: getClientId() },
        });
        if (cancelled) return;
        setLikes(data.likes || 0);
        setViews(data.views || 0);
        setLiked(!!data.liked);
      } catch (err) {
        console.error("Failed to load post engagement:", err);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    const prev = { liked, likes };
    // Optimistic update
    setLiked(!liked);
    setLikes((n) => Math.max(0, n + (liked ? -1 : 1)));
    try {
      const { data } = await api.post(`/posts/${slug}/like`, { client_id: getClientId() });
      setLiked(!!data.liked);
      setLikes(data.likes || 0);
    } catch {
      // rollback on failure
      setLiked(prev.liked);
      setLikes(prev.likes);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex items-center gap-6 text-xs tracking-[0.22em] uppercase text-[#5C6670]"
      data-testid="post-engagement"
    >
      <span data-testid="post-views-badge" className="flex items-center gap-2">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
        {(views || 0).toLocaleString()} {views === 1 ? "read" : "reads"}
      </span>

      <button
        type="button"
        onClick={toggle}
        disabled={busy || !ready}
        aria-pressed={liked}
        aria-label={liked ? "Unlike this entry" : "Like this entry"}
        data-testid="post-like-btn"
        className={`group flex items-center gap-2 transition-colors ${liked ? "text-[#C5A059]" : "text-[#5C6670] hover:text-[#C5A059]"}`}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill={liked ? "currentColor" : "none"}
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`transition-transform ${busy ? "" : "group-active:scale-125"}`}
          aria-hidden="true"
        >
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
        <span data-testid="post-like-count">
          {(likes || 0).toLocaleString()} {likes === 1 ? "like" : "likes"}
        </span>
      </button>
    </div>
  );
}
